import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useBusinessExpenseTypes, useBusinessExpenseSubtypes } from "@/hooks/data";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import { useBulkUpdateBusinessExpenses } from "@/hooks/useBulkUpdateBusinessExpenses";
import { BUSINESS_PAYMENT_TYPES } from "@/constants/businessExpenses";
import { isContractActive } from "@/utils/contractUtils";

const NO_CHANGE = "__NO_CHANGE__";

interface BulkEditBusinessExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedExpenseIds: string[];
}

export const BulkEditBusinessExpenseModal = ({
  isOpen,
  onClose,
  selectedExpenseIds,
}: BulkEditBusinessExpenseModalProps) => {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(NO_CHANGE);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>(NO_CHANGE);
  const [selectedTypeId, setSelectedTypeId] = useState<string>(NO_CHANGE);
  const [selectedSubtypeId, setSelectedSubtypeId] = useState<string>(NO_CHANGE);
  const [selectedPaymentType, setSelectedPaymentType] = useState<string>(NO_CHANGE);

  const { data: organizations } = useOrganizations();
  const { data: properties } = useProperties();
  const { data: contracts } = useContracts();
  const { data: expenseTypes } = useBusinessExpenseTypes();
  const { data: allSubtypes } = useBusinessExpenseSubtypes();
  const bulkUpdateMutation = useBulkUpdateBusinessExpenses();

  // Filter properties by selected organization
  const filteredProperties = properties?.filter((property) => {
    if (selectedOrganizationId === NO_CHANGE) return true;
    const activeContract = contracts?.find(
      (c) => c.property_id === property.id && isContractActive(c)
    );
    return activeContract?.organization_id === selectedOrganizationId;
  });

  // Filter subtypes by selected type
  const filteredSubtypes = allSubtypes?.filter(
    (subtype) => selectedTypeId === NO_CHANGE || subtype.type_id === selectedTypeId
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedOrganizationId(NO_CHANGE);
      setSelectedPropertyId(NO_CHANGE);
      setSelectedTypeId(NO_CHANGE);
      setSelectedSubtypeId(NO_CHANGE);
      setSelectedPaymentType(NO_CHANGE);
    }
  }, [isOpen]);

  // Reset property when organization changes
  useEffect(() => {
    if (selectedOrganizationId !== NO_CHANGE) {
      setSelectedPropertyId(NO_CHANGE);
    }
  }, [selectedOrganizationId]);

  // Reset subtype when type changes
  useEffect(() => {
    if (selectedTypeId !== NO_CHANGE) {
      setSelectedSubtypeId(NO_CHANGE);
    }
  }, [selectedTypeId]);

  const handleSubmit = () => {
    // Validar que tipo y subtipo estén en sincronía
    if (selectedTypeId !== NO_CHANGE && selectedSubtypeId === NO_CHANGE) {
      return; // No enviar si hay tipo pero no subtipo
    }

    bulkUpdateMutation.mutate(
      {
        expenseIds: selectedExpenseIds,
        updates: {
          organization_id: selectedOrganizationId,
          property_id: selectedPropertyId,
          expense_type_id: selectedTypeId,
          expense_subtype_id: selectedSubtypeId,
          payment_type: selectedPaymentType,
        },
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    setSelectedOrganizationId(NO_CHANGE);
    setSelectedPropertyId(NO_CHANGE);
    setSelectedTypeId(NO_CHANGE);
    setSelectedSubtypeId(NO_CHANGE);
    setSelectedPaymentType(NO_CHANGE);
    onClose();
  };

  const hasChanges =
    selectedOrganizationId !== NO_CHANGE ||
    selectedPropertyId !== NO_CHANGE ||
    selectedTypeId !== NO_CHANGE ||
    selectedSubtypeId !== NO_CHANGE ||
    selectedPaymentType !== NO_CHANGE;

  const isValid =
    selectedTypeId === NO_CHANGE || selectedSubtypeId !== NO_CHANGE;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editar egresos seleccionados</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Organización</Label>
            <Select value={selectedOrganizationId} onValueChange={setSelectedOrganizationId}>
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                {organizations?.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Propiedad</Label>
            <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                {filteredProperties?.map((property) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.shortName || property.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Tipo de Egreso</Label>
            <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                {expenseTypes?.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Subtipo de Egreso</Label>
            <Select
              value={selectedSubtypeId}
              onValueChange={setSelectedSubtypeId}
              disabled={selectedTypeId === NO_CHANGE}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                {filteredSubtypes?.map((subtype) => (
                  <SelectItem key={subtype.id} value={subtype.id}>
                    {subtype.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTypeId !== NO_CHANGE && selectedSubtypeId === NO_CHANGE && (
              <p className="text-sm text-destructive">
                Debe seleccionar un subtipo de egreso
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Tipo de Pago</Label>
            <Select value={selectedPaymentType} onValueChange={setSelectedPaymentType}>
              <SelectTrigger>
                <SelectValue placeholder="Sin cambios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHANGE}>Sin cambios</SelectItem>
                {BUSINESS_PAYMENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasChanges || !isValid || bulkUpdateMutation.isPending}
          >
            {bulkUpdateMutation.isPending ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
